<script setup>
import { computed, ref, onMounted } from 'vue';
import { useUserStore } from '@/store/user-store';
import { useRouter } from 'vue-router';

const store = useUserStore();
const router = useRouter();
const scrollContainer = ref(null);
const showTooltip = ref({});
const showGapTooltip = ref(false); // Tooltip for Remaining Gap / Surplus
const showDebtRatioTooltip = ref(false); // Tooltip for Debt to Income Ratio
const isRecommendationExpanded = ref(false); // Accordion state for recommendation (collapsed by default)
const isImpactExpanded = ref(false); // Accordion state for impact (collapsed by default)

// Check if custGoals exist in dashboardData and load goalSummary if needed
onMounted(async () => {
  const custGoals = store.dashboardData?.custGoals || [];
  const hasGoalsInDashboard = custGoals.length > 0;
  const hasGoalSummaryLoaded = store.goalSummary && store.goalSummary !== "";

  console.log('TopDashboardCards mounted - custGoals:', custGoals);
  console.log('Has goals in dashboard:', hasGoalsInDashboard);
  console.log('Has goalSummary loaded:', hasGoalSummaryLoaded);

  // If goals exist in dashboardData but goalSummary is not loaded, fetch it
  if (hasGoalsInDashboard && !hasGoalSummaryLoaded) {
    console.log('Loading goal summary because custGoals exist...');
    await store.loadGoalSummary();
    console.log('Goal summary loaded:', store.goalSummary);
  }
});

// Computed: goalSummary from store
const goalSummary = computed(() => store.goalSummary);

// Check if goalSummary is loaded (not null/empty string)
const hasGoalSummary = computed(() => {
  return goalSummary.value && goalSummary.value !== "";
});

// Check if goals are set - align with GoalDashboard.vue logic
const hasGoals = computed(() => {
  // If no goalSummary loaded yet, return false
  if (!hasGoalSummary.value) return false;

  // Check if there are actual goals in the modes.perGoalSIP.goals array
  const goals = goalSummary.value?.modes?.perGoalSIP?.goals;
  console.log('goal summary:', goalSummary.value); // Debug log to verify structure
  return goals && Array.isArray(goals) && goals.length > 0;
});

// Computed mappings (CORRECTED PATHS - data is under modes.perGoalSIP)
const thisMonth = computed(() => {
  return goalSummary.value?.modes?.perGoalSIP?.thisMonth || null;
});

const metrics = computed(() => thisMonth.value?.metrics || {});
const cash = computed(() => goalSummary.value?.cashSavingsProfile || {});
const financialHealth = computed(() => store.financialStatement?.financialHealth || {});

// Calculate weighted goal required rate of return
const weightedGoalReturn = computed(() => {
  const allGoals = goalSummary.value?.modes?.perGoalSIP?.goals || [];
  if (allGoals.length === 0) return 0;

  const totalTarget = allGoals.reduce((sum, goal) => sum + (goal.targetFV || 0), 0);
  if (totalTarget === 0) return 0;

  const weightedReturn = allGoals.reduce((sum, goal) => {
    const weight = (goal.targetFV || 0) / totalTarget;
    const expectedReturn = goal.expectedReturnPercent || 0; // Already a percentage (e.g., 11)
    return sum + (weight * expectedReturn);
  }, 0);

  return weightedReturn; // Returns percentage value (e.g., 11)
});

// Get latest portfolio 6-month CAGR (weighted average of Stocks, MF, and Fixed Income)
const portfolio6MonthCAGR = computed(() => {
  const dailySummary = store.dashboardData?.portfolioDailySummary || [];
  if (dailySummary.length === 0) return 0;

  // Filter out zero-value entries and sort by date descending
  const validSummaries = dailySummary.filter(s => s.portfolioTotalMarketVal > 0);
  const sorted = [...validSummaries].sort((a, b) => {
    const dateA = new Date(a.lastUpdated);
    const dateB = new Date(b.lastUpdated);
    return dateB - dateA; // Descending order
  });

  return sorted[0]?.portfolioXIRR || 0; // 6-month weighted average CAGR (e.g., 5.78)
});

// Calculate return gap and risk level
const returnGap = computed(() => portfolio6MonthCAGR.value - weightedGoalReturn.value);
const returnGapPercentage = computed(() => {
  if (weightedGoalReturn.value === 0) return 0;
  return (returnGap.value / weightedGoalReturn.value) * 100;
});

// Determine if return analysis is meaningful
const shouldShowReturnAnalysis = computed(() => {
  // Hide return analysis if portfolio CAGR is 0 (no tracking) AND goal is fully funded (no SIP needed)
  const noTrackingHistory = portfolio6MonthCAGR.value === 0;
  const goalFullyFunded = (metrics.value.requiredSipForTargetConfidence || 0) === 0;

  return !(noTrackingHistory && goalFullyFunded);
});

// Get component returns for detailed analysis
const componentReturns = computed(() => {
  const dailySummary = store.dashboardData?.portfolioDailySummary || [];
  if (dailySummary.length === 0) return { stockXIRR: 0, mutualFundXRR: 0, otherXRR: 0 };

  const validSummaries = dailySummary.filter(s => s.portfolioTotalMarketVal > 0);
  const sorted = [...validSummaries].sort((a, b) => {
    const dateA = new Date(a.lastUpdated);
    const dateB = new Date(b.lastUpdated);
    return dateB - dateA;
  });

  const latest = sorted[0] || {};
  return {
    stockXIRR: latest.stockXIRR || 0,
    mutualFundXRR: latest.mutualFundXRR || 0,
    otherXRR: latest.otherXRR || 0
  };
});

// Determine risk level and contextual message based on gap
const returnRiskLevel = computed(() => {
  const gap = returnGapPercentage.value;
  const cagr = portfolio6MonthCAGR.value;
  const { stockXIRR, mutualFundXRR, otherXRR } = componentReturns.value;

  // Status determination
  let status, color, icon;
  if (gap >= 0) {
    status = 'healthy';
    color = '#10b981';
    icon = 'pi-check-circle';
  } else if (Math.abs(gap) <= 20) {
    status = 'caution';
    color = '#f59e0b';
    icon = 'pi-exclamation-triangle';
  } else {
    status = 'critical';
    color = '#ef4444';
    icon = 'pi-times-circle';
  }

  // Contextual message - concise version
  let message = '';
  let recommendation = '';

  if (gap >= 5) {
    message = '✅ Outperforming goals';
    recommendation = 'Maintain discipline. Review if equity exceeds risk profile.';
  } else if (gap >= 0) {
    message = '✅ On track with goals';
    recommendation = 'Continue SIP. Review annually.';
  } else if (gap >= -10) {
    message = '⚠️ Slightly behind';
    recommendation = 'Consider 10-15% SIP increase or rebalance.';
  } else if (gap >= -20) {
    message = '⚠️ Lagging goals';
    recommendation = 'Review allocation. Increase equity if horizon permits.';
  } else {
    message = '🚨 Action needed';
    recommendation = 'Consult adviser. Increase SIP or adjust goals.';
  }

  // Build component breakdown (only show non-zero components)
  let breakdown = '';
  const components = [];
  if (stockXIRR !== 0) components.push(`Stocks: ${stockXIRR.toFixed(1)}%`);
  if (mutualFundXRR !== 0) components.push(`MF: ${mutualFundXRR.toFixed(1)}%`);
  if (otherXRR !== 0) components.push(`FI: ${otherXRR.toFixed(1)}%`);

  if (components.length > 0) {
    breakdown = components.join(' • ');
  }

  // Market context - very concise
  let marketNote = '';
  if (cagr >= 15) {
    marketNote = '🔥 Exceptional returns - high volatility';
  } else if (cagr >= 8) {
    marketNote = '📈 Strong growth phase';
  } else if (cagr >= 3) {
    marketNote = '📊 Moderate growth';
  } else if (cagr > -3) {
    marketNote = '📉 Stable, modest gains';
  } else if (cagr > -8) {
    marketNote = '⚠️ Mild correction phase';
  } else {
    marketNote = '🛡️ Bear phase - stay invested';
  }

  return {
    status,
    color,
    icon,
    message,
    recommendation,
    breakdown,
    marketNote
  };
});

// Get all goals sorted by due date (closest first)
const sortedGoals = computed(() => {
  const allGoals = goalSummary.value?.modes?.perGoalSIP?.goals || [];
  return [...allGoals].sort((a, b) => {
    const dateA = new Date(a.dueDate);
    const dateB = new Date(b.dueDate);
    return dateA - dateB;
  });
});

// Formatting helpers
const inr = (amount) => {
  if (!amount && amount !== 0) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const pct = (prob) => {
  if (!prob && prob !== 0) return '0%';
  return `${Math.round(prob * 100)}%`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// Status pill computation
const statusPill = computed(() => {
  const gap = metrics.value.remainingGapForTargetConfidence || 0;
  if (gap <= 0) {
    return { label: 'ON TRACK', color: '#10b981' };
  }
  return { label: 'AT RISK', color: '#ef4444' };
});

// Navigate to goals
const navigateToGoals = () => {
  // Change the active tab in the store (for sidebar highlighting)
  store.setTabView('My Goals');
};

// Scroll carousel
const scrollLeft = () => {
  if (!scrollContainer.value) return;
  const cardWidth = scrollContainer.value.offsetWidth / 2; // Scroll by ~1 card width
  scrollContainer.value.scrollBy({ left: -cardWidth, behavior: 'smooth' });
};

const scrollRight = () => {
  if (!scrollContainer.value) return;
  const cardWidth = scrollContainer.value.offsetWidth / 2;
  scrollContainer.value.scrollBy({ left: cardWidth, behavior: 'smooth' });
};

// Toggle tooltip
const toggleTooltip = (goalName) => {
  showTooltip.value[goalName] = !showTooltip.value[goalName];
};
</script>

<template>
  <!-- Render in two cases: 1) goalSummary loaded, or 2) no goalSummary at all (show banner) -->
  <div class="top-dashboard-cards">
    <!-- Empty State: No Goals Set (show if no goalSummary OR goalSummary exists but no goals) -->
    <div v-if="!hasGoalSummary || !hasGoals" class="no-goals-banner">
      <div class="no-goals-icon">
        <i class="pi pi-flag"></i>
      </div>
      <div class="no-goals-content">
        <h3 class="no-goals-title">Set Your Financial Goals</h3>
        <p class="no-goals-description">
          Define your financial aspirations—retirement, children's education, home purchase, or any life milestone.
          Our SEBI-registered advisers will craft a personalized investment plan with realistic SIP recommendations
          and success probabilities tailored to your goals.
        </p>
        <button class="no-goals-button" @click="navigateToGoals">
          <i class="pi pi-plus-circle"></i>
          Declare Your Goals
        </button>
      </div>
    </div>

    <!-- Carousel with Cards (if goals exist) -->
    <div v-else class="carousel-wrapper">
      <button
        class="carousel-arrow carousel-arrow-left"
        @click="scrollLeft"
        aria-label="Scroll cards left"
      >
        <i class="pi pi-chevron-left"></i>
      </button>

      <div class="carousel-container" ref="scrollContainer">
        <!-- Mobile Swipe Indicator -->
        <div class="mobile-swipe-indicator">
          <i class="pi pi-angle-double-right"></i>
          <span>Swipe to see more</span>
        </div>

        <!-- CARD 1: This Month - One Clear Decision -->
        <div class="dashboard-card card-primary">
          <div class="card-header header-primary">
            <div class="card-header-left">
              <i class="pi pi-compass"></i>
              <span class="card-title">This Month: One Clear Decision</span>
            </div>
            <span class="status-pill" :style="{ backgroundColor: statusPill.color }">
              {{ statusPill.label }}
            </span>
          </div>

          <div class="card-body">
            <!-- Compact Accordion 1: Recommendation -->
            <div class="compact-accordion">
              <div class="compact-accordion-header" @click="isRecommendationExpanded = !isRecommendationExpanded">
                <div class="accordion-left">
                  <i class="pi pi-lightbulb"></i>
                  <span class="accordion-label">Action Plan</span>
                </div>
                <i class="pi accordion-chevron" :class="isRecommendationExpanded ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
              </div>
              <div class="compact-accordion-content" v-show="isRecommendationExpanded">
                <p class="recommendation-text">
                  {{ thisMonth?.recommendationText || 'No recommendation available' }}
                </p>
              </div>
            </div>

            <!-- Compact Accordion 2: Impact -->
            <div class="compact-accordion" v-if="thisMonth?.expectedImpact">
              <div class="compact-accordion-header" @click="isImpactExpanded = !isImpactExpanded">
                <div class="accordion-left">
                  <i class="pi pi-bolt"></i>
                  <span class="accordion-label">Expected Impact</span>
                </div>
                <i class="pi accordion-chevron" :class="isImpactExpanded ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
              </div>
              <div class="compact-accordion-content" v-show="isImpactExpanded">
                <p class="recommendation-impact-text">
                  {{ thisMonth?.expectedImpact || '' }}
                </p>
              </div>
            </div>

            <div class="metrics-grid">
              <div class="metric-item">
                <div class="metric-label-with-tooltip">
                  <span class="metric-label">Current SIP</span>
                  <div class="tooltip-container">
                    <button
                      class="tooltip-icon"
                      @click="showTooltip.currentSip = !showTooltip.currentSip"
                      aria-label="Current SIP info"
                    >
                      <i class="pi pi-info-circle"></i>
                    </button>
                    <div v-if="showTooltip.currentSip" class="gap-tooltip-popover">
                      <p><strong>Current SIP:</strong> Your actual monthly SIP investment (₹{{ inr(metrics.currentTotalSip) }}).</p>
                      <p v-if="metrics.weightedAvgSipReturnPercent"><strong>Avg Return:</strong> {{ metrics.weightedAvgSipReturnPercent.toFixed(1) }}% (adjusted to ₹{{ inr(metrics.cumulativeCurrentSipContribution) }} for goal comparison).</p>
                    </div>
                  </div>
                </div>
                <span class="metric-value">{{ inr(metrics.currentTotalSip) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-label">Additional Savings</span>
                <span class="metric-value metric-success">{{ inr(metrics.monthlyLiquidSavings) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-label">Target SIP (80%)</span>
                <span class="metric-value">{{ inr(metrics.requiredSipForTargetConfidence) }}</span>
              </div>
              <div class="metric-item" :class="metrics.remainingGapForTargetConfidence < 0 ? 'metric-item-surplus' : ''">
                <div class="metric-label-with-tooltip">
                  <span class="metric-label">
                    {{ metrics.remainingGapForTargetConfidence < 0 ? 'Available Surplus' : 'Remaining Gap' }}
                  </span>
                  <div class="tooltip-container">
                    <button
                      class="tooltip-icon"
                      @click="showGapTooltip = !showGapTooltip"
                      aria-label="Show gap explanation"
                    >
                      <i class="pi pi-info-circle"></i>
                    </button>

                    <!-- Tooltip for Gap/Surplus -->
                    <div v-if="showGapTooltip" class="gap-tooltip-popover">
                      <p v-if="metrics.remainingGapForTargetConfidence < 0">
                        <strong>Available Surplus:</strong> Extra monthly savings beyond what's needed to reach 80% confidence.
                      </p>
                      <p v-else>
                        <strong>Remaining Gap:</strong> Additional SIP needed (return-adjusted) to reach 80% confidence target.
                      </p>
                      <p v-if="metrics.performanceAdjustedGap && Math.abs(metrics.performanceAdjustedGap - metrics.remainingGapForTargetConfidence) > 100" class="tooltip-note">
                        <em>Return-adjusted gap: ₹{{ inr(Math.abs(metrics.performanceAdjustedGap)) }}</em>
                      </p>
                    </div>
                  </div>
                </div>

                <span
                  class="metric-value"
                  :class="metrics.remainingGapForTargetConfidence < 0 ? 'metric-success' : 'metric-error'"
                >
                  {{ inr(Math.abs(metrics.remainingGapForTargetConfidence)) }}
                </span>
              </div>
            </div>
            <div class="metrics-note">
              *All figures monthly
            </div>
          </div>
        </div>

        <!-- CARD 2, 3, etc.: Goal Health Cards (one per goal) -->
        <div
          v-for="goal in sortedGoals"
          :key="goal.goalName"
          class="dashboard-card card-goal"
        >
          <div class="card-header header-goal">
            <div class="card-header-left">
              <i class="pi pi-trophy"></i>
              <span class="card-title">Goal Health: {{ goal.goalName }}</span>
            </div>
            <span class="goal-date">{{ formatDate(goal.dueDate) }}</span>
          </div>

          <div class="card-body">
            <div class="goal-target">
              <span class="target-label">Target</span>
              <span class="target-value">{{ inr(goal.targetFV) }}</span>
            </div>

            <div class="goal-metrics">
              <div class="goal-metric-row">
                <span class="goal-metric-label">Success Probability</span>
                <span class="goal-metric-value probability-value">
                  {{ pct(goal.stochasticAnalysis?.sipScenarios?.realistic?.actualProbability) }}
                </span>
              </div>

              <div class="goal-metric-row">
                <span class="goal-metric-label">Recommended SIP</span>
                <span class="goal-metric-value">
                  {{ inr(goal.stochasticAnalysis?.sipScenarios?.realistic?.sip) }}
                </span>
              </div>

              <div class="goal-metric-row">
                <span class="goal-metric-label">Gap to 80% (vs. Current SIP)</span>
                <span class="goal-metric-value gap-value">
                  {{ inr(goal.stochasticAnalysis?.liquidSavingsAssessment?.sip80_actualWeightedSIP_gap) }}
                </span>
              </div>
            </div>

            <div class="range-band-section">
              <div class="range-band-header">
                <span class="range-band-label">RANGE BAND (P5/P50/P95)</span>
                <button
                  class="info-icon"
                  @click="toggleTooltip(goal.goalName)"
                  aria-label="Show range band explanation"
                >
                  <i class="pi pi-info-circle"></i>
                </button>
              </div>

              <!-- Tooltip -->
              <div v-if="showTooltip[goal.goalName]" class="tooltip-popover">
                <p><strong>P5 (Worst Case):</strong> In the worst 5 out of 100 scenarios, your investment may achieve this value. Not guaranteed.</p>
                <p><strong>P50 (Typical):</strong> The median outcome—half the scenarios perform better, half worse.</p>
                <p><strong>P95 (Best Case):</strong> In the best 5 out of 100 scenarios, your investment may reach this value. Not guaranteed.</p>
              </div>

              <div class="range-chips">
                <span class="range-chip chip-p5">
                  {{ inr(goal.stochasticAnalysis?.sipScenarios?.realistic?.scenarios?.p5?.finalValue) }}
                </span>
                <span class="range-chip chip-p50">
                  {{ inr(goal.stochasticAnalysis?.sipScenarios?.realistic?.scenarios?.p50?.finalValue) }}
                </span>
                <span class="range-chip chip-p95">
                  {{ inr(goal.stochasticAnalysis?.sipScenarios?.realistic?.scenarios?.p95?.finalValue) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- CARD LAST: Safety & Red Flags -->
        <div class="dashboard-card card-safety">
          <div class="card-header header-safety">
            <div class="card-header-left">
              <i class="pi pi-shield"></i>
              <span class="card-title">Safety & Red Flags</span>
            </div>
          </div>

          <div class="card-body">
            <div class="safety-items">
              <div class="safety-item">
                <div class="safety-indicator indicator-success"></div>
                <div class="safety-content">
                  <span class="safety-label">Emergency Fund</span>
                  <span class="safety-value">{{ inr(cash.emergencyFund) }}</span>
                </div>
              </div>

              <div class="safety-item">
                <div
                  class="safety-indicator"
                  :class="(financialHealth.debtToIncomeRatio || 0) < 0.4 ? 'indicator-success' : 'indicator-error'"
                ></div>
                <div class="safety-content">
                  <div class="safety-label-with-tooltip">
                    <span class="safety-label">Debt to Income Ratio</span>
                    <div class="tooltip-container">
                      <button
                        class="tooltip-icon"
                        @click="showDebtRatioTooltip = !showDebtRatioTooltip"
                        aria-label="Show debt ratio explanation"
                      >
                        <i class="pi pi-info-circle"></i>
                      </button>

                      <!-- Tooltip for Debt Ratio -->
                      <div v-if="showDebtRatioTooltip" class="debt-ratio-tooltip-popover">
                        <p>
                          <strong>Debt to Income Ratio:</strong> Portion of income for debt payments. Keep below 40% (lower is better).
                        </p>
                      </div>
                    </div>
                  </div>
                  <span
                    class="safety-value"
                    :style="{
                      color: (financialHealth.debtToIncomeRatio || 0) < 0.4 ? '#10b981' : '#ef4444'
                    }"
                  >
                    {{ ((financialHealth.debtToIncomeRatio || 0) * 100).toFixed(1) }}%
                  </span>
                </div>
              </div>

              <!-- Return Comparison Section (only show when meaningful) -->
              <div v-if="shouldShowReturnAnalysis" class="return-comparison-section" :class="`return-${returnRiskLevel.status}`">
                <div class="return-comparison-header">
                  <span class="comparison-label">6-Month Performance Review</span>
                  <div class="risk-indicator" :style="{ backgroundColor: returnRiskLevel.color }">
                    <i class="pi" :class="returnRiskLevel.icon"></i>
                  </div>
                </div>

                <div class="return-comparison-grid">
                  <div class="return-item return-portfolio">
                    <span class="return-label">Portfolio 6M CAGR</span>
                    <span class="return-value">{{ portfolio6MonthCAGR.toFixed(2) }}%</span>
                    <span class="return-sublabel" v-if="returnRiskLevel.breakdown">{{ returnRiskLevel.breakdown }}</span>
                  </div>
                  <div class="return-divider">
                    <i class="pi pi-arrow-right"></i>
                  </div>
                  <div class="return-item return-goal">
                    <span class="return-label">Goal Required</span>
                    <span class="return-value">{{ weightedGoalReturn.toFixed(2) }}%</span>
                    <span class="return-sublabel">Annualized Target</span>
                  </div>
                </div>

                <div class="return-gap-indicator" :class="`gap-${returnRiskLevel.status}`">
                  <span class="gap-text">
                    {{ returnGap >= 0 ? 'Ahead' : 'Behind' }} by {{ Math.abs(returnGap).toFixed(2) }}%
                  </span>
                </div>

                <!-- Contextual Message & Recommendation -->
                <div class="return-context">
                  <div class="context-message">
                    {{ returnRiskLevel.message }}
                  </div>
                  <div class="context-recommendation">
                    <i class="pi pi-info-circle"></i>
                    <span>{{ returnRiskLevel.recommendation }}</span>
                  </div>
                  <div class="context-volatility">
                    <i class="pi pi-chart-line"></i>
                    <span>{{ returnRiskLevel.marketNote }}</span>
                  </div>
                </div>
              </div>

              <!-- Goal Opportunity Section (show when return analysis is hidden) -->
              <div v-else class="goal-opportunity-section">
                <div class="opportunity-header">
                  <span class="opportunity-label">Goal Diversification Opportunity</span>
                  <div class="opportunity-indicator">
                    <i class="pi pi-lightbulb"></i>
                  </div>
                </div>

                <div class="opportunity-content">
                  <div class="opportunity-stat">
                    <span class="stat-label">Success Probability</span>
                    <span class="stat-value success-prob">{{ sortedGoals[0]?.stochasticAnalysis?.actualSipProbability?.probabilityOfSuccess ? (sortedGoals[0].stochasticAnalysis.actualSipProbability.probabilityOfSuccess * 100).toFixed(0) : '95' }}%</span>
                  </div>
                  <div class="opportunity-message">
                    <i class="pi pi-info-circle"></i>
                    <span>Your current assets fully fund this goal. Consider diversifying across multiple financial goals to optimize your wealth.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        class="carousel-arrow carousel-arrow-right"
        @click="scrollRight"
        aria-label="Scroll cards right"
      >
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
.top-dashboard-cards {
  margin-bottom: 30px;
}

/* No Goals Banner */
.no-goals-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #f59e0b;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 24px;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.15);
}

.no-goals-icon {
  flex-shrink: 0;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.no-goals-icon i {
  font-size: 1.5rem;
  color: white;
}

.no-goals-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.no-goals-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #78350f;
  margin: 0;
}

.no-goals-description {
  font-size: 0.95rem;
  color: #92400e;
  line-height: 1.5;
  margin: 0;
}

.no-goals-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--an-primary, #0d7c68);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
  box-shadow: 0 2px 8px rgba(13, 124, 104, 0.3);
}

.no-goals-button:hover {
  background: var(--an-primary-dark, #0a6454);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 124, 104, 0.4);
}

/* Carousel Wrapper */
.carousel-wrapper {
  position: relative;
  margin-top: 24px;
}

.carousel-container {
  display: flex;
  gap: 20px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  padding: 10px 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.carousel-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

/* Carousel Arrows */
.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.carousel-arrow:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.carousel-arrow i {
  font-size: 1rem;
  color: #374151;
}

.carousel-arrow-left {
  left: -20px;
}

.carousel-arrow-right {
  right: -20px;
}

/* Dashboard Card Base */
.dashboard-card {
  flex: 0 0 48%;
  min-width: 48%;
  scroll-snap-align: start;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: visible;
  display: flex;
  flex-direction: column;
  height: auto;
  min-height: 320px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  color: white;
  font-weight: 600;
}

.card-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-header-left i {
  font-size: 1.2rem;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
}

.card-body {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: visible;
}

/* Card 1: Primary (This Month) */
.card-primary .header-primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.status-pill {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Compact Accordion Styles */
.compact-accordion {
  margin-bottom: 8px;
}

.compact-accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 36px;
}

.compact-accordion-header:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.accordion-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.accordion-left i {
  font-size: 0.9rem;
  color: #3b82f6;
  flex-shrink: 0;
}

.accordion-left i.pi-bolt {
  color: #f59e0b;
}

.accordion-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.2;
}

.accordion-chevron {
  font-size: 0.75rem;
  color: #64748b;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.compact-accordion-content {
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  margin-top: -1px;
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.recommendation-text,
.recommendation-impact-text {
  font-size: 0.875rem;
  color: #334155;
  line-height: 1.5;
  margin: 0;
  font-weight: 400;
}

/* Mobile Swipe Indicator */
.mobile-swipe-indicator {
  display: none;
  position: fixed;
  bottom: 20px;
  right: 10px;
  background: rgba(14, 165, 233, 0.95);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  z-index: 100;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
  animation: swipePulse 2s ease-in-out infinite;
  pointer-events: none;
}

@keyframes swipePulse {
  0%, 100% {
    opacity: 0.9;
    transform: translateY(-50%) translateX(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-50%) translateX(5px);
  }
}

.mobile-swipe-indicator i {
  font-size: 1.2rem;
  animation: swipeArrow 1.5s ease-in-out infinite;
}

@keyframes swipeArrow {
  0%, 100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(5px);
  }
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 8px;
  position: relative;
  z-index: 1;
}

.metric-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  position: relative;
  overflow: visible;
}

.metric-item-surplus {
  background: #d1fae5 !important; /* Solid light green background for surplus */
  border: 1px solid #10b981;
}

.metric-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.metric-label-with-tooltip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.tooltip-container {
  position: relative;
  display: flex;
  align-items: center;
  z-index: 100;
}

.tooltip-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: #6b7280;
  font-size: 0.875rem;
  transition: color 0.2s ease;
}

.tooltip-icon:hover {
  color: #3b82f6;
}

.gap-tooltip-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 240px;
  background: #1f2937;
  color: white;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 0.7rem;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  animation: fadeIn 0.2s ease;
  pointer-events: auto;
}

.gap-tooltip-popover::before {
  content: '';
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #1f2937;
}

.gap-tooltip-popover p {
  margin: 0;
  text-align: left;
}

.gap-tooltip-popover strong {
  display: block;
  margin-bottom: 4px;
  color: #fbbf24;
  font-size: 0.75rem;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.metric-value {
  font-size: 1.125rem;
  color: #1f2937;
  font-weight: 700;
}

.metric-success {
  color: #10b981;
}

.metric-error {
  color: #ef4444;
}

.metrics-note {
  font-size: 0.6875rem;
  color: #9ca3af;
  font-style: italic;
  text-align: right;
  margin-top: -8px;
}

/* Card 2+: Goal Health Cards */
.card-goal .header-goal {
  background: linear-gradient(135deg, #10b981, #059669);
}

.goal-date {
  font-size: 0.875rem;
  font-weight: 600;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
}

.goal-target {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  background: #f0fdf4;
  border-radius: 8px;
  border-left: 4px solid #10b981;
}

.target-label {
  font-size: 0.875rem;
  color: #065f46;
  font-weight: 600;
}

.target-value {
  font-size: 1.25rem;
  color: #047857;
  font-weight: 700;
}

.goal-metrics {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.goal-metric-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e5e7eb;
}

.goal-metric-row:last-child {
  border-bottom: none;
}

.goal-metric-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.goal-metric-value {
  font-size: 1rem;
  color: #1f2937;
  font-weight: 700;
}

.probability-value {
  color: #f59e0b;
  font-size: 1.125rem;
}

.gap-value {
  color: #ef4444;
}

.range-band-section {
  margin-top: 8px;
}

.range-band-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.range-band-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-icon {
  background: none;
  border: none;
  cursor: pointer;
  color: #3b82f6;
  font-size: 1rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.info-icon:hover {
  color: #2563eb;
}

.tooltip-popover {
  background: #1f2937;
  color: white;
  padding: 12px;
  border-radius: 8px;
  font-size: 0.8125rem;
  line-height: 1.5;
  margin-bottom: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.tooltip-popover p {
  margin: 0 0 8px 0;
}

.tooltip-popover p:last-child {
  margin-bottom: 0;
}

.range-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.range-chip {
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 700;
  color: white;
  flex: 1;
  text-align: center;
  min-width: 90px;
}

.chip-p5 {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.chip-p50 {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.chip-p95 {
  background: linear-gradient(135deg, #10b981, #059669);
}

/* Card Last: Safety */
.card-safety .header-safety {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.safety-items {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.safety-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
}

.safety-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.indicator-success {
  background: #10b981;
}

.indicator-info {
  background: #3b82f6;
}

.indicator-error {
  background: #ef4444;
}

.safety-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}

.safety-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.safety-label-with-tooltip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.debt-ratio-tooltip-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 240px;
  background: #1f2937;
  color: white;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 0.7rem;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  animation: fadeIn 0.2s ease;
  pointer-events: auto;
}

.debt-ratio-tooltip-popover::before {
  content: '';
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #1f2937;
}

.debt-ratio-tooltip-popover p {
  margin: 0;
  text-align: left;
}

.debt-ratio-tooltip-popover strong {
  display: block;
  margin-bottom: 4px;
  color: #fbbf24;
  font-size: 0.75rem;
}

.safety-value {
  font-size: 1rem;
  color: #1f2937;
  font-weight: 700;
}

/* Return Comparison Section */
.return-comparison-section {
  margin-top: 8px;
  padding: 10px;
  border-radius: 8px;
  border-left: 3px solid #10b981;
}

.return-comparison-section.return-healthy {
  background: #f0fdf4;
  border-left-color: #10b981;
}

.return-comparison-section.return-caution {
  background: #fffbeb;
  border-left-color: #f59e0b;
}

.return-comparison-section.return-critical {
  background: #fef2f2;
  border-left-color: #ef4444;
}

.return-comparison-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.comparison-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #065f46;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.return-healthy .comparison-label {
  color: #065f46;
}

.return-caution .comparison-label {
  color: #78350f;
}

.return-critical .comparison-label {
  color: #991b1b;
}

.risk-indicator {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.risk-indicator i {
  color: white;
  font-size: 0.65rem;
}

.return-comparison-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 6px;
  align-items: center;
  margin-bottom: 8px;
}

.return-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  background: white;
  border-radius: 6px;
}

.return-portfolio {
  border: 2px solid #3b82f6;
}

.return-goal {
  border: 2px solid #f59e0b;
}

.return-label {
  font-size: 0.65rem;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}

.return-sublabel {
  font-size: 0.5rem;
  color: #9ca3af;
  font-weight: 500;
  margin-top: 2px;
}

.return-value {
  font-size: 1rem;
  color: #1f2937;
  font-weight: 700;
}

.return-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
}

.return-divider i {
  font-size: 0.875rem;
}

.return-gap-indicator {
  padding: 6px 8px;
  border-radius: 6px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}

.return-gap-indicator.gap-healthy {
  background: #d1fae5;
  border: 1px solid #10b981;
}

.return-gap-indicator.gap-caution {
  background: #fef3c7;
  border: 1px solid #f59e0b;
}

.return-gap-indicator.gap-critical {
  background: #fee2e2;
  border: 1px solid #ef4444;
}

.gap-text {
  font-size: 0.75rem;
  font-weight: 700;
  color: #1f2937;
}

.gap-warning {
  font-size: 0.7rem;
  color: #991b1b;
  font-weight: 600;
}

/* Return Context Styling */
.return-context {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}

.context-message {
  font-size: 0.75rem;
  font-weight: 600;
  color: #1f2937;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  line-height: 1.3;
}

.context-recommendation,
.context-volatility {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.65rem;
  color: #4b5563;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  line-height: 1.4;
}

.context-recommendation i,
.context-volatility i {
  font-size: 0.7rem;
  margin-top: 1px;
  flex-shrink: 0;
  opacity: 0.7;
}

/* Goal Opportunity Section */
.goal-opportunity-section {
  margin-top: 8px;
  padding: 10px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-left: 3px solid #3b82f6;
}

.opportunity-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.opportunity-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #1e40af;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.opportunity-indicator {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
}

.opportunity-indicator i {
  color: white;
  font-size: 0.65rem;
}

.opportunity-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.opportunity-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  background: white;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
}

.stat-label {
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 600;
}

.stat-value {
  font-size: 1rem;
  color: #1f2937;
  font-weight: 700;
}

.success-prob {
  color: #10b981;
}

.opportunity-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  font-size: 0.7rem;
  line-height: 1.4;
  color: #334155;
}

.opportunity-message i {
  color: #3b82f6;
  font-size: 0.75rem;
  margin-top: 2px;
  flex-shrink: 0;
}

/* Responsive */
@media (max-width: 1024px) {
  .dashboard-card {
    flex: 0 0 70%;
    min-width: 70%;
  }
}

@media (max-width: 768px) {
  .dashboard-card {
    flex: 0 0 100%;
    min-width: 100%;
  }

  .carousel-arrow {
    display: none;
  }

  /* Show swipe indicator on mobile */
  .mobile-swipe-indicator {
    display: flex;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .no-goals-banner {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .no-goals-button {
    align-self: stretch;
    justify-content: center;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .carousel-container {
    scroll-behavior: auto;
  }

  .carousel-arrow,
  .no-goals-button {
    transition: none;
  }
}
</style>
